import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaClock,
  FaUser,
  FaComments
} from 'react-icons/fa';
import CustomImage from '../common/CustomImage';
import './JobCard.css';
import { createSlug } from '../../utils/urlUtils';

const JobCard = ({ job }) => {
  // Helper function to strip HTML tags from text
  const stripHtmlTags = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Helper function to format date
  const formatDate = (date) => {
    if (!date) return 'Recently posted';
    
    try {
      const postedDate = new Date(date);
      
      // Check if date is valid
      if (isNaN(postedDate.getTime())) {
        return 'Recently posted';
      }
      
      const now = new Date();
      
      // Set time to start of day for accurate day comparison (UTC)
      const today = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
      const postDay = new Date(postedDate.getUTCFullYear(), postedDate.getUTCMonth(), postedDate.getUTCDate());
      
      const diffTime = today - postDay;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Handle future dates
      if (diffDays < 0) {
        return 'Recently posted';
      }
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
      }
      if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} month${months > 1 ? 's' : ''} ago`;
      }
      
      // For future dates or very old dates, show the actual date
      return postedDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Recently posted';
    }
  };

  // Get company name from new structure
  const getCompanyName = () => {
    return job.hiringOrganization?.name || job.company || 'Company';
  };

  // Get company logo from new structure
  const getCompanyLogo = () => {
    return job.hiringOrganization?.logo || job.companyLogo || job.logo;
  };

  // Get clean description
  const getCleanDescription = () => {
    const description = job.description || job.jobDescription || '';
    const cleanText = stripHtmlTags(description);
    return cleanText.length > 120 ? cleanText.substring(0, 120) + '...' : cleanText;
  };

  // Get author
  const getAuthor = () => {
    return job.author || job.postedBy || job.recruiter || 'JobsOpportunity.in';
  };

  // Get comments count
  const getCommentsCount = () => {
    return job.comments?.length || job.commentCount || 0;
  };

  const jobUrl = `/jobs/${job?.slug || createSlug(job?.title)}`;

  return (
    <div className="job-card">
      <div className="job-card-header">
        <Link to={jobUrl} className="company-logo-container">
          <CustomImage
            src={getCompanyLogo()}
            alt={getCompanyName()}
            className="company-logo"
            fallbackSrc="/images/company-placeholder.png"
          />
        </Link>
        <div className="job-title-container">
          <h2 className="job-title">
            <Link to={jobUrl}>
              {job?.title}
            </Link>
          </h2>
          <div className="job-meta">
            <div className="meta-item">
              <FaClock className="meta-icon" />
              <span>{formatDate(job?.datePosted || job?.postedDate || job?.createdAt || job?.date)}</span>
            </div>
            <div className="meta-item">
              <FaUser className="meta-icon" />
              <span>{getAuthor()}</span>
            </div>
            <div className="meta-item">
              <FaComments className="meta-icon" />
              <span>{getCommentsCount()} comments</span>
            </div>
          </div>
          {job.description && (
            <Link to={jobUrl} className="job-description">
              <p>{getCleanDescription()}</p>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobCard;
